import { useState } from "react";
import Alert from "@amzn/meridian/esm/components/alert";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { MAX_ORG_PILLS, orgColor, WHOS_WHO_STATE } from "./whosWho.constants";
import { THEME_COLOR } from "../../styles/colors";
import InviteeCard from "./InviteeCard";
import WhosWhoOrgPills from "./WhosWhoOrgPills";
import WhosWhoOrgSelect from "./WhosWhoOrgSelect";
import SkeletonCard from "./SkeletonCard";
import LegacyWhosWhoPanel from "./legacy/WhosWhoPanel";

const accentFor = (person, orgColorMap) => orgColorMap[person.org] || THEME_COLOR.MIST_GRAY;

const WhosWhoPanel = (props) => {
  const { state, invitees } = props;
  const featureFlags = useFeatureFlags();
  const [orgFilter, setOrgFilter] = useState(null);

  // TODO(MEETEX-299408): Remove the legacy fallback after V2 rollout.
  if (featureFlags?.isWhosWhoV2Enabled !== true) {
    return <LegacyWhosWhoPanel {...props} />;
  }

  const hasInvitees = Boolean(invitees && invitees.length > 0);

  const orgCounts = (invitees || []).reduce((counts, person) => {
    if (!person.external && person.org) {
      counts[person.org] = (counts[person.org] ?? 0) + 1;
    }
    return counts;
  }, {});
  const orgsList = Object.keys(orgCounts).sort((left, right) => left.localeCompare(right));

  const orgColorMap = {};
  orgsList.forEach((org, i) => {
    orgColorMap[org] = orgColor(i);
  });

  const visibleInvitees = !hasInvitees
    ? []
    : orgFilter
      ? invitees.filter((p) => !p.external && p.org === orgFilter)
      : invitees;

  if (state === WHOS_WHO_STATE.LOADING) {
    return (
      <Column spacing="300" spacingInset="300 none none none">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </Column>
    );
  }

  const isError = state === WHOS_WHO_STATE.ERROR;

  return (
    <Column spacing="300" spacingInset="none 100">
      {isError && (
        <Alert type="warning" size="small">
          Some profile details are temporarily unavailable.
        </Alert>
      )}

      {hasInvitees && (
        <>
          <Row spacing="200" alignmentVertical="center" wrap="down">
            <Text type="h100">
              {orgFilter
                ? `${visibleInvitees.length} of ${invitees.length} participants`
                : `${invitees.length} participants`}
            </Text>
            <Text type="b100" color="secondary">Powered by Aza</Text>
          </Row>

          {orgsList.length > MAX_ORG_PILLS ? (
            <WhosWhoOrgSelect
              orgs={orgsList}
              orgColorMap={orgColorMap}
              orgCounts={orgCounts}
              totalCount={invitees.length}
              activeOrg={orgFilter}
              onSelect={setOrgFilter}
            />
          ) : (
            <WhosWhoOrgPills
              orgs={orgsList}
              orgColorMap={orgColorMap}
              activeOrg={orgFilter}
              onSelect={setOrgFilter}
            />
          )}

          {visibleInvitees.map((person) => (
            <InviteeCard
              key={person.id || person.alias || person.email}
              person={person}
              isError={isError}
              accentColor={accentFor(person, orgColorMap)}
            />
          ))}
        </>
      )}
    </Column>
  );
};

export default WhosWhoPanel;
